import { Activity, AlertTriangle, Gauge, Trophy } from 'lucide-react';

import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';
import { Badge } from '@/ui/base/badge';

import { QuadroDeEstado, Secao } from './componentes';
import { GraficoDeTendencia } from './graficos';
import type { IndicadoresDeDesempenho, LinhaDePenalidade, LinhaDoRanking } from './contrato';
import { carregarIndicadores } from './dados';
import { useColecao } from './useColecao';

/**
 * ONDA-CL3 — indicadores de desempenho (KPIs). Tela única, sem aba.
 *
 * Os quatro destaques vêm formatados do dado ("67,2%", "16 dias") porque a
 * unidade é parte do indicador; a tela não recalcula nem reformata.
 */

const ICONES: readonly StatItem['icon'][] = [Gauge, Activity, Trophy, AlertTriangle];

function indicadoresVazios(dado: IndicadoresDeDesempenho): boolean {
  return dado.destaques.length === 0 && dado.ranking.length === 0 && dado.penalidades.length === 0;
}

const COLUNAS_RANKING: DataTableColumn<LinhaDoRanking>[] = [
  { header: '#', accessorKey: 'posicao', sortKey: 'posicao' },
  { header: 'Credenciado', accessorKey: 'credenciado', sortKey: 'credenciado' },
  { header: 'OS', accessorKey: 'ordensServico', sortKey: 'ordensServico' },
  { header: 'Valor', cell: (l) => <MoneyDisplay value={l.valor} /> },
];

const COLUNAS_PENALIDADES: DataTableColumn<LinhaDePenalidade>[] = [
  { header: 'Credenciado', accessorKey: 'credenciado', sortKey: 'credenciado' },
  { header: 'Multas', accessorKey: 'multas', sortKey: 'multas' },
  { header: 'Ocorrências', accessorKey: 'ocorrencias', sortKey: 'ocorrencias' },
  {
    header: 'Total',
    cell: (l) => <Badge variant={l.total > 8 ? 'destructive' : 'secondary'}>{l.total}</Badge>,
  },
];

export function IndicadoresPage() {
  const { estado, recarregar } = useColecao(carregarIndicadores, indicadoresVazios);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Análises e notificações"
        title="Indicadores de Desempenho"
        description="KPIs e métricas do sistema TEG usados como sinais contínuos de aderência, gargalo e evolução operacional"
      />

      <QuadroDeEstado
        estado={estado}
        recarregar={recarregar}
        tituloVazio="Sem indicadores apurados"
        descricaoVazio="Nenhuma competência apurada ainda. Os indicadores aparecem quando houver movimento fechado."
      >
        {(dado) => (
          <div className="space-y-4">
            <StatsGrid
              stats={dado.destaques.map((destaque, i) => ({
                title: destaque.rotulo,
                value: destaque.valor,
                description: destaque.descricao,
                icon: ICONES[i % ICONES.length] ?? Gauge,
              }))}
            />

            {/* Gráfico de linha, não tabela: é assim que o protótipo
                desenha esta seção. */}
            <Secao titulo="Tendência Mensal (Últimos 6 meses)">
              <GraficoDeTendencia dados={dado.tendenciaMensal} />
            </Secao>

            <Secao titulo="Ranking de Credenciados">
              <DataTable
                columns={COLUNAS_RANKING}
                data={[...dado.ranking]}
                emptyTitle="Sem credenciado no ranking"
                emptyDescription="Nenhum credenciado com ordem de serviço no período."
                ariaLabel="Ranking de credenciados"
              />
            </Secao>

            <Secao titulo="Credenciados com Mais Penalidades">
              <DataTable
                columns={COLUNAS_PENALIDADES}
                data={[...dado.penalidades]}
                emptyTitle="Nenhuma penalidade registrada"
                emptyDescription="Não houve multa nem ocorrência no período."
                ariaLabel="Credenciados com mais penalidades"
              />
            </Secao>
          </div>
        )}
      </QuadroDeEstado>
    </div>
  );
}

export default IndicadoresPage;
